import util from '../util';
import giftColorThresholds from '../GiftThresholds';
import Widget from './Widget';

const BASE_CSS = `
.hm-widget {
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	box-sizing: border-box;
	pointer-events: none;
	overflow: hidden;
}
.hm-widget.va-top    { justify-content: flex-start; }
.hm-widget.va-middle { justify-content: center; }
.hm-widget.ha-left   { align-items: flex-start; }
.hm-widget.ha-right  { align-items: flex-end; }
.hm-widget.ha-center { align-items: center; }
.hm-widget > .hm-msg {
	max-width: 100%;
	box-sizing: border-box;
	overflow-wrap: break-word;
	word-break: break-word;
}
.hm-avatar-fallback {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #241e32;
	overflow: hidden;
}
.hm-avatar-fallback svg { width: 70%; height: 70%; }
.hm-content .emote { vertical-align: middle; max-width: 1.6em; max-height: 1.6em; }
.hm-content .sticker, .hm-content .image { width: 6.4em; height: 6.4em; object-fit: contain; vertical-align: middle; }
/* One shared rule set for all 4 styles, since every one of them puts spans inside a
   .hm-content box — no per-style placement needed. One or two gift images float beside
   the text at the same size as an inline sticker/image span above; the moment a 3rd
   shows up, :has() switches the whole block to its own centered row below the text
   instead, shrinking a notch further per :has(:nth-child(N)) breakpoint. Same technique
   the chat widget/log use for their own gift image stacks. */
/* flow-root: a float-only .chatImages (a gift with no message text) would otherwise not
   contribute to .hm-content's height at all, since a floated child is out of normal flow
   — the box would collapse to its padding and the icon would visually spill out below it. */
.hm-content { display: flow-root; }
.hm-content > .chatImages { float: right; margin: 0 0 .2em .3em; }
.hm-content > .chatImages > .chatImage { width: 6.4em; height: 6.4em; object-fit: contain; }
.hm-content > .chatImages:has(.chatImage:nth-child(3)) {
	float: none;
	clear: both;
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	align-items: center;
	gap: .2em;
	margin: .3em 0 0;
}
.hm-content > .chatImages:has(.chatImage:nth-child(3))  > .chatImage { width: 4.6em; height: 4.6em; }
.hm-content > .chatImages:has(.chatImage:nth-child(4))  > .chatImage { width: 3.9em; height: 3.9em; }
.hm-content > .chatImages:has(.chatImage:nth-child(5))  > .chatImage { width: 3.4em; height: 3.4em; }
.hm-content > .chatImages:has(.chatImage:nth-child(6))  > .chatImage { width: 3.0em; height: 3.0em; }
.hm-content > .chatImages:has(.chatImage:nth-child(7))  > .chatImage { width: 2.7em; height: 2.7em; }
.hm-content > .chatImages:has(.chatImage:nth-child(8))  > .chatImage { width: 2.4em; height: 2.4em; }
.hm-content > .chatImages:has(.chatImage:nth-child(9))  > .chatImage { width: 2.2em; height: 2.2em; }
.hm-content > .chatImages:has(.chatImage:nth-child(10)) > .chatImage { width: 2.0em; height: 2.0em; }
.hm-content > .chatImages > .chatImages-overflow { font-weight: bold; opacity: .85; padding: 0 .2em; }
`;

/* ─── service icon positioning helpers ────────────────────────
   Round: center icon on circle edge at 45° (lower-right)
   Square: center icon exactly on the lower-right corner
────────────────────────────────────────────────────────────── */
const SERVICE_ICON_ROUND = `
.hm-service-icon {
	position: absolute;
	width: 38%;
	height: 38%;
	right: 0;
	bottom: 0;
	object-fit: contain;
}`;

const SERVICE_ICON_SQUARE = `
.hm-service-icon {
	position: absolute;
	width: 38%;
	height: 38%;
	right: 0;
	bottom: 0;
	object-fit: contain;
}`;

/* ─── per-style CSS ───────────────────────────────────────── */

const STYLE_CSS = {

/* ── BUBBLE ──
   Round avatar inside blue pill; white rounded content box below.
   Pill left-edges at ~avatar-radius from widget edge.
   Content indented by avatar width.
────────────────────────────────────────────────────────────── */
bubble: `
.hm-msg {
	display: inline-flex;
	flex-direction: column;
	align-items: flex-start;
	max-width: 100%;
}
.hm-title {
	background-color: var(--hm-title-bg);
	color: var(--hm-title-color);
	border-radius: 200px;
	display: flex;
	align-items: center;
	padding: .2em .8em .2em .2em;
	gap: .45em;
	position: relative;
	z-index: 2;
	bottom: -0.55em;
	max-width: 100%;
}
.hm-avatar-wrap {
	position: relative;
	width: 1.8em;
	height: 1.8em;
	flex-shrink: 0;
}
.hm-avatar-wrap > img:not(.hm-service-icon) {
	width: 100%;
	height: 100%;
	border-radius: 50%;
	object-fit: cover;
	display: block;
}
.hm-avatar-fallback { border-radius: 50%; }
${SERVICE_ICON_ROUND}
.hm-sender { margin: 0; }
.hm-content {
	background-color: var(--hm-text-bg);
	color: var(--hm-text-color);
	padding: .85em 1.1em;
	border-radius: 0 90px 90px 90px;
	position: relative;
	z-index: 1;
	margin-left: 1.5em;
	max-width: calc(100% - 1.5em);
	box-shadow: rgba(0,0,0,.18) -4px 5px 24px -4px;
}
.hm-content:empty { display: none; }
.hm-widget.ha-right  .hm-msg  { align-items: flex-end; }
.hm-widget.ha-center .hm-msg  { align-items: center; }
.hm-widget.ha-right  .hm-content { border-radius: 90px 0 90px 90px; margin: 0 1.5em 0 0; }
.hm-widget.ha-center .hm-content { border-radius: 90px; margin: 0; }
.hm-msg:not(.gift):not(.chat) .hm-title   { background-color: var(--hm-alert-title-bg) !important; color: var(--hm-alert-title-color) !important; }
.hm-msg:not(.gift):not(.chat) .hm-content { background-color: var(--hm-gift-bg) !important; color: var(--hm-gift-color) !important; }
`,

/* ── BLOCKS ──
   Avatar is position:absolute (left:0, top:0, z-index:4). ::before creates
   the brand-color shadow shifted 0.42em right+down, extending below avatar.
   Title starts at margin-left:5em (right of avatar), auto-width.
   Content starts at margin-left:2.5em (half avatar), padding-left clears avatar.
   flex-direction:column; align-items:flex-start so title is narrow, content wide.
────────────────────────────────────────────────────────────── */
blocks: `
.hm-msg {
	display: inline-flex;
	flex-direction: column;
	position: relative;
	overflow: hidden;
	min-height: 5em;
}
.hm-avatar-wrap {
	position: absolute;
	left: 0;
	top: 0;
	width: 5em;
	height: 5em;
	z-index: 4;
}
.hm-avatar-wrap::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0.42em;
	width: 100%;
	height: calc(100% + 0.42em);
	background-color: var(--hm-title-bg);
}
.hm-avatar-wrap > img:not(.hm-service-icon) {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
	z-index: 1;
}
.hm-avatar-fallback { border-radius: 0; position: absolute; inset: 0; z-index: 1; }
${SERVICE_ICON_SQUARE}
.hm-service-icon { z-index: 2; }
.hm-title {
	display: flex;
	align-items: center;
	margin-left: 5em;
	min-height: 2.3em;
	padding: 0 1.25em;
	background-color: var(--hm-title-bg);
	color: var(--hm-title-color);
	position: relative;
	z-index: 2;
	flex-shrink: 0;
}
.hm-sender { margin: 0; font-weight: bold; }
.hm-content {
	margin-left: 2.5em;
	padding: .625em 1em .625em 3.75em;
	background-color: var(--hm-text-bg);
	color: var(--hm-text-color);
	position: relative;
	z-index: 3;
	margin-top: -1px;
	box-sizing: border-box;
}
.hm-content:empty { display: none; }
.hm-msg:not(.gift):not(.chat) .hm-avatar-wrap::before { background-color: var(--hm-alert-title-bg) !important; }
.hm-msg:not(.gift):not(.chat) .hm-title   { background-color: var(--hm-alert-title-bg) !important; color: var(--hm-alert-title-color) !important; }
.hm-msg:not(.gift):not(.chat) .hm-content { background-color: var(--hm-gift-bg) !important; color: var(--hm-gift-color) !important; }
.hm-widget.ha-right .hm-avatar-wrap { left: auto; right: 0; }
.hm-widget.ha-right .hm-avatar-wrap::before { left: auto; right: 0.42em; }
.hm-widget.ha-right .hm-title { margin-left: 0; margin-right: 5em; }
.hm-widget.ha-right .hm-content { margin-left: 0; margin-right: 2.5em; padding-left: 1em; padding-right: 3.75em; }
`,

/* ── MINIMAL ──
   Full-width dark background. Colored vertical accent bar (brand color)
   at left edge (position:absolute). Square avatar (slight border-radius).
   Name + message stacked on the right, all text on same dark background.
   Layout: WrapBackground fills full size, BorderLine at left:0,
   WrapInner has padding-left = 2×bar-width to clear the bar + gap.
────────────────────────────────────────────────────────────── */
minimal: `
.hm-msg {
	display: inline-flex;
	flex-direction: row;
	align-items: flex-start;
	position: relative;
	overflow: hidden;
	background-color: var(--hm-text-bg);
	color: var(--hm-text-color);
	padding: .43em .57em .43em 1.14em;
	box-sizing: border-box;
}
.hm-msg::before {
	content: '';
	position: absolute;
	left: 0;
	top: 0;
	bottom: 0;
	width: .57em;
	background-color: var(--hm-title-bg);
}
.hm-avatar-wrap {
	position: relative;
	flex-shrink: 0;
	width: 3.5em;
	height: 3.5em;
	margin-right: .71em;
}
.hm-avatar-wrap > img:not(.hm-service-icon) {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
	border-radius: .14em;
}
.hm-avatar-fallback { border-radius: .14em; }
${SERVICE_ICON_SQUARE}
.hm-text-area {
	flex: 0 1 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	overflow: hidden;
}
.hm-title {
	background: transparent;
}
.hm-sender { margin: 0 0 .1em; font-weight: bold; }
.hm-content {
	background: transparent;
	color: inherit;
	padding: 0;
	margin: 0;
	overflow: hidden;
}
.hm-content:empty { display: none; }
.hm-msg:not(.gift):not(.chat) { background-color: var(--hm-gift-bg) !important; color: var(--hm-gift-color) !important; }
.hm-msg:not(.gift):not(.chat)::before { background-color: var(--hm-alert-title-bg) !important; }
.hm-msg:not(.gift):not(.chat) .hm-title { color: var(--hm-alert-title-color) !important; }
.hm-widget.ha-right .hm-msg { flex-direction: row-reverse; padding: .43em 1.14em .43em .57em; }
.hm-widget.ha-right .hm-msg::before { left: auto; right: 0; }
.hm-widget.ha-right .hm-avatar-wrap { margin-right: 0; margin-left: .71em; }
`,

/* ── CLASSIC ──
   Avatar is position:absolute (left:0, top:0). TextWrap has margin-left:3.375em
   so it starts slightly behind the avatar. Title bar is ~92% width (not full),
   creating a "step" where content is wider. No clip-path — just a shorter width
   with a rounded top-right corner. Matches StreamYard Classic layout exactly.
────────────────────────────────────────────────────────────── */
classic: `
.hm-msg {
	position: relative;
	width: 100%;
	min-height: 5em;
	box-sizing: border-box;
}
.hm-avatar-wrap {
	position: absolute;
	left: 0;
	top: 0;
	width: 5em;
	height: 5em;
	border-radius: 50%;
	background-color: var(--hm-title-bg);
	padding: .2em;
	box-sizing: border-box;
	z-index: 2;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: visible;
}
.hm-avatar-wrap > img:not(.hm-service-icon) {
	width: 100%;
	height: 100%;
	border-radius: 50%;
	object-fit: cover;
	display: block;
}
.hm-avatar-fallback { border-radius: 50%; }
.hm-service-icon {
	position: absolute;
	width: 33%;
	height: 33%;
	right: 3.6%;
	bottom: 3.6%;
	object-fit: contain;
}
.hm-text-wrap {
	margin-left: 3.375em;
}
.hm-title {
	background-color: var(--hm-title-bg);
	color: var(--hm-title-color);
	display: flex;
	align-items: center;
	margin-top: .6em;
	width: calc(100% - 2.1em);
	padding: .1em .6em .1em 1.875em;
	border-radius: 0 .3em 0 0;
	box-sizing: border-box;
}
.hm-sender { margin: 0; }
.hm-content {
	background-color: var(--hm-text-bg);
	color: var(--hm-text-color);
	padding: .3em .3em .3em 1.875em;
	border-radius: .3em;
	box-sizing: border-box;
}
.hm-content:empty { display: none; }
.hm-msg:not(.gift):not(.chat) .hm-avatar-wrap { background-color: var(--hm-alert-title-bg) !important; }
.hm-msg:not(.gift):not(.chat) .hm-title   { background-color: var(--hm-alert-title-bg) !important; color: var(--hm-alert-title-color) !important; }
.hm-msg:not(.gift):not(.chat) .hm-content { background-color: var(--hm-gift-bg) !important; color: var(--hm-gift-color) !important; }
.hm-widget.ha-right .hm-avatar-wrap { left: auto; right: 0; }
.hm-widget.ha-right .hm-text-wrap { margin-left: 0; margin-right: 3.375em; }
.hm-widget.ha-right .hm-title { margin-left: 2.1em; padding: .1em 1.875em .1em .6em; border-radius: .3em 0 0 0; }
.hm-widget.ha-right .hm-content { padding: .3em 1.875em .3em .3em; }
`,

};

const AVATAR_SVG = `<svg fill="#eee9f0" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
<path d="m 256,17.712357 c 60.75077,0 109.99959,40.491541 109.99959,112.288053 0,71.79651 -49.24882,130.00041 -109.99959,130.00041 -60.75077,0 -109.99959,-58.2039 -109.99959,-130.00041 C 146.00041,58.203898 195.24923,17.712357 256,17.712357 Z M 442.19077,512 H 69.809232 c -18.67159,0 -33.808411,-15.13682 -33.808411,-33.80841 0,-87.36657 98.495999,-188.6034 219.999179,-188.6034 121.50318,0 219.99918,101.23683 219.99918,188.6034 C 475.99918,496.86318 460.864,512 442.19077,512 Z"/>
</svg>`;

export default class HighlightedMessageWidget extends Widget {
	constructor(opts) {
		super(opts);
		this._msgElem         = null;
		this._currentMsgId    = null;
		this._currentPrepared = null;
	}


	async init(initialData = {}) {
		if(window?.BroadcastChannel) {
			this._bc = new BroadcastChannel('LiveJoiner');
			this._bc.addEventListener('message', event => {
				if(event.data?.type === 'highlightedMessage')
					this._handleHighlight(event.data?.data);
			});
		}
		await super.init(initialData);
	}

	_build() {
		this._setCSS('hmBaseCSS', BASE_CSS);
		this.widgetElem = document.createElement('div');
		this.widgetElem.className = 'hm-widget';
		this.container.appendChild(this.widgetElem);
	}

	destroy() {
		this._bc?.close();
		document.getElementById('hmBaseCSS')?.remove();
		document.getElementById('hmStyle')?.remove();
		document.getElementById('googleFontLink')?.remove();
		super.destroy();
	}

	applyUserData(data) {
		super.applyUserData(data);
		if(data?.highlighted_message) this._handleHighlight(data.highlighted_message);
	}

	applySettings(settings) {
		const prevStyle = this.settings.style;
		super.applySettings(settings);
		this._updateWidgetClasses();
		if(this._msgElem && this._currentPrepared && this.settings.style !== prevStyle) {
			this.widgetElem.innerHTML = '';
			this._msgElem = this._buildMessageElem(this._currentPrepared, this.settings.style || 'bubble');
			this.widgetElem.appendChild(this._msgElem);
		}
	}


	_generateCSS() {
		const s = this.settings;

		this._updateFontLink();

		const textDecoration = [
			s.underlineText     ? 'underline'    : null,
			s.strikethroughText ? 'line-through' : null,
		].filter(Boolean).join(' ');

		const style = s.style || 'bubble';
		this._setCSS('hmStyle', `
			:root {
				--animate-duration:      ${s.duration         || 500}ms;
				--hm-title-bg:           ${s.titleBg          || 'rgba(0,128,0,1)'};
				--hm-title-color:        ${s.titleColor       || 'rgba(255,255,255,1)'};
				--hm-text-bg:            ${s.textBg           || 'rgba(0,128,0,1)'};
				--hm-text-color:         ${s.textColor        || 'rgba(255,255,255,1)'};
				--hm-gift-bg:            ${s.giftBg           || 'rgba(255,195,0,1)'};
				--hm-gift-color:         ${s.giftColor        || 'rgba(0,0,0,1)'};
				--hm-alert-title-bg:     ${s.alertTitleBg     || s.titleBg    || 'rgba(0,128,0,1)'};
				--hm-alert-title-color:  ${s.alertTitleColor  || s.titleColor || 'rgba(255,255,255,1)'};
			}
			.hm-widget {
				padding-left:   ${s.paddingLeft   || 0}px;
				padding-right:  ${s.paddingRight  || 0}px;
				padding-top:    ${s.paddingBottom || 0}px;
				padding-bottom: ${s.paddingBottom || 0}px;
				font-family:    ${s.fontFamily ? `'${s.fontFamily}'` : 'inherit'};
				font-size:      ${s.fontSize || 20}px;
				letter-spacing: ${s.letterSpacing || 0}px;
				${s.boldText      ? 'font-weight: bold;'                  : ''}
				${s.italicText    ? 'font-style: italic;'                 : ''}
				${textDecoration  ? `text-decoration: ${textDecoration};` : ''}
				${s.uppercaseText ? 'text-transform: uppercase;'          : ''}
			}
			${STYLE_CSS[style] || STYLE_CSS.bubble}
			${giftColorThresholds.map(t => [
				style !== 'minimal' ? `.hm-msg.gift.gift-${t.value} .hm-title { background-color: ${t.secondaryColor} !important; color: ${t.textColor} !important; }` : '',
				style !== 'minimal' ? `.hm-msg.gift.gift-${t.value} .hm-content { background-color: ${t.color} !important; color: ${t.textColor} !important; }` : '',
				style === 'blocks'  ? `.hm-msg.gift.gift-${t.value} .hm-avatar-wrap::before { background-color: ${t.secondaryColor} !important; }` : '',
				style === 'classic' ? `.hm-msg.gift.gift-${t.value} .hm-avatar-wrap { background-color: ${t.secondaryColor} !important; }` : '',
				style === 'minimal' ? `.hm-msg.gift.gift-${t.value} { background-color: ${t.color} !important; color: ${t.textColor} !important; }` : '',
				style === 'minimal' ? `.hm-msg.gift.gift-${t.value}::before { background-color: ${t.secondaryColor} !important; }` : '',
			].filter(Boolean).join('\n')).join('\n')}
			${s.blurRadius > 0 || s.spreadRadius > 0 ? `.hm-msg { filter: ${util.generateSpreadShadowFilter(s.spreadRadius, s.blurRadius, s.shadowColor)}; }` : ''}
			${s.extraCSS || ''}
		`);
	}

	_updateWidgetClasses() {
		const va = this.settings.verticalAlign || 'bottom';
		const ha = this.settings.textAlign     || 'left';
		this.widgetElem.className = `hm-widget va-${va} ha-${ha}`;
	}

	_handleHighlight(message) {
		if(!message) {
			if(this._msgElem) {
				const el = this._msgElem;
				this._msgElem = null;
				this._currentMsgId = null;
				this._currentPrepared = null;
				this._startExit(el);
			}
			return;
		}
		const prepared = util.prepareMessage(message.message || message, {
			id: true, serviceId: true, serviceName: true, serviceIconUrl: true,
			avatarUrl: true, displayName: true, spansHtml: true,
			giftString: true, eventString: true, classList: true, giftColorThreshold: true,
			images: true,
		}, {
			giftTypes: this.giftTypes,
			eventTypes: this.eventTypes,
			serviceIcons: this.serviceIcons,
			user: this.userData,
			giftColorThresholds,
			giftMediaThresholds: this.giftThresholds,
			textReplacements: this.textReplacements,
		});

		if(this._msgElem && prepared.id && prepared.id === this._currentMsgId) {
			// already showing — nothing to do, server manages expiry
		} else {
			this._showMessage(prepared);
		}
	}

	_showMessage(prepared) {
		const msgElem = this._buildMessageElem(prepared, this.settings.style || 'bubble');
		const oldElem = this._msgElem;

		this._msgElem = msgElem;
		this._currentMsgId = prepared.id || null;
		this._currentPrepared = prepared;

		if(oldElem) this._startExit(oldElem);

		// Stack above any elements still playing exit animations
		if(this.widgetElem.children.length > 0) {
			msgElem.style.position = 'relative';
			msgElem.style.zIndex   = '2';
		}
		this.widgetElem.appendChild(msgElem);

		if(this.settings.inTransition) {
			msgElem.classList.add('animate__animated', `animate__${this.settings.inTransition}`);
			msgElem.addEventListener('animationend', () => {
				msgElem.classList.remove('animate__animated', `animate__${this.settings.inTransition}`);
				msgElem.style.position = '';
				msgElem.style.zIndex   = '';
			}, { once: true });
		} else {
			msgElem.style.position = '';
			msgElem.style.zIndex   = '';
		}
	}

	// Pins el at its current rendered position and plays the exit animation (or removes immediately).
	// Cancels any in-progress enter animation first so the element exits from where it currently is.
	// Each element calls this at most once — no global "hiding" flag needed.
	_startExit(el) {
		if(!el) return;
		if(this.settings.inTransition) {
			el.classList.remove('animate__animated', `animate__${this.settings.inTransition}`);
		}
		if(el.style.position !== 'absolute') {
			const top = el.offsetTop, left = el.offsetLeft, width = el.offsetWidth, height = el.offsetHeight;
			el.style.position = 'absolute';
			el.style.top    = top    + 'px';
			el.style.left   = left   + 'px';
			el.style.width  = width  + 'px';
			el.style.height = height + 'px';
		}
		el.style.zIndex = '1';
		if(this.settings.outTransition) {
			el.classList.add('animate__animated', `animate__${this.settings.outTransition}`);
			el.addEventListener('animationend', () => el.remove(), { once: true });
		} else {
			el.remove();
		}
	}

	/* ── DOM builders ──────────────────────────────────────── */

	_buildMessageElem(prepared, style) {
		const div = document.createElement('div');
		const classes = (prepared.classList || []).filter(c => c !== 'message');
		div.className = ['hm-msg', ...classes].join(' ');
		switch(style) {
			case 'blocks':  this._buildBlocks(div, prepared);  break;
			case 'minimal': this._buildMinimal(div, prepared); break;
			case 'classic': this._buildClassic(div, prepared); break;
			default:        this._buildBubble(div, prepared);  break;
		}
		return div;
	}

	_buildBubble(div, prepared) {
		const title = document.createElement('div');
		title.className = 'hm-title';
		title.appendChild(this._buildAvatarWrap(prepared));
		title.appendChild(this._buildSenderP(prepared));
		div.appendChild(title);
		div.appendChild(this._buildContent(prepared));
	}

	_buildBlocks(div, prepared) {
		div.appendChild(this._buildAvatarWrap(prepared));
		const title = document.createElement('div');
		title.className = 'hm-title';
		title.appendChild(this._buildSenderP(prepared));
		div.appendChild(title);
		div.appendChild(this._buildContent(prepared));
	}

	_buildMinimal(div, prepared) {
		div.appendChild(this._buildAvatarWrap(prepared));
		const textArea = document.createElement('div');
		textArea.className = 'hm-text-area';
		const title = document.createElement('div');
		title.className = 'hm-title';
		title.appendChild(this._buildSenderP(prepared));
		textArea.appendChild(title);
		textArea.appendChild(this._buildContent(prepared));
		div.appendChild(textArea);
	}

	_buildClassic(div, prepared) {
		div.appendChild(this._buildAvatarWrap(prepared));
		const textWrap = document.createElement('div');
		textWrap.className = 'hm-text-wrap';
		const title = document.createElement('div');
		title.className = 'hm-title';
		title.appendChild(this._buildSenderP(prepared));
		textWrap.appendChild(title);
		textWrap.appendChild(this._buildContent(prepared));
		div.appendChild(textWrap);
	}

	_buildAvatarWrap(prepared) {
		const wrap = document.createElement('div');
		wrap.className = 'hm-avatar-wrap';

		if(prepared.avatarUrl) {
			const img = document.createElement('img');
			img.src = prepared.avatarUrl;
			img.alt = '';
			wrap.appendChild(img);
		} else {
			const fallback = document.createElement('div');
			fallback.className = 'hm-avatar-fallback';
			fallback.innerHTML = AVATAR_SVG;
			wrap.appendChild(fallback);
		}

		if(prepared.serviceIconUrl) {
			const si = document.createElement('img');
			si.src = prepared.serviceIconUrl;
			si.alt = prepared.serviceName || '';
			si.className = 'hm-service-icon';
			wrap.appendChild(si);
		}

		return wrap;
	}

	_buildSenderP(prepared) {
		const p = document.createElement('p');
		p.className = 'hm-sender';
		p.textContent = [prepared.displayName, prepared.giftString, prepared.eventString]
			.filter(Boolean).join(' ');
		return p;
	}

	_buildContent(prepared) {
		const div = document.createElement('div');
		div.className = 'hm-content';
		div.innerHTML = prepared.spansHtml || '';
		// prepareMessage already sizes `images` to the gift's own multiplier (the same
		// cascade the chat widget/log use), so requesting it is enough to pick up the
		// total — nothing multiplier-specific needed here beyond rendering the result.
		const urls = (prepared.images || []).filter(img => img?.url).map(img => img.url);
		if(urls.length) util.placeChatImages(div, urls);
		return div;
	}
}
